import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ExerciseDetail({ user }) {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [painLevel, setPainLevel] = useState([0]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchExercise();
  }, [exerciseId]);

  const fetchExercise = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/exercises/${exerciseId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setExercise(data);
        
        // Check if access is required but user doesn't have it
        if (data.access_required && !user) {
          toast.error('Please login to access this content');
          navigate('/');
        } else if (data.access_required) {
          toast.error('You need to purchase this course to access the video');
        }
      } else if (response.status === 401) {
        toast.error('Please login to view this exercise');
        navigate('/');
      } else if (response.status === 403) {
        toast.error('You need to purchase this course to access the video');
      }
    } catch (error) {
      toast.error('Failed to load exercise');
    } finally {
      setLoading(false);
    }
  };

  const handleLogProgress = async () => {
    if (!user) {
      toast.error('Please login to log progress');
      navigate('/');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          exercise_id: exerciseId,
          completed,
          pain_level: painLevel[0],
          notes
        })
      });

      if (response.ok) {
        toast.success('Progress logged successfully!');
        setCompleted(false);
        setPainLevel([0]);
        setNotes('');
      }
    } catch (error) {
      toast.error('Failed to log progress');
    } finally {
      setSubmitting(false);
    }
  };

  const getYoutubeEmbedUrl = (url) => {
    const videoId = url.split('v=')[1]?.split('&')[0] || url.split('/').pop();
    return `https://www.youtube.com/embed/${videoId}`;
  };

  if (loading) {
    return <div className="min-h-screen bg-[#F4EFE6] flex items-center justify-center">Loading...</div>;
  }

  if (!exercise) {
    return <div className="min-h-screen bg-[#F4EFE6] flex items-center justify-center">Exercise not found</div>;
  }

  return (
    <div className="min-h-screen bg-[#F4EFE6]">
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
        <div className="container mx-auto">
          <Button data-testid="back-btn" onClick={() => navigate(-1)} variant="ghost" className="rounded-full px-2 sm:px-4">
            <ArrowLeft className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Back</span>
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-12" data-testid="exercise-detail">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="mb-6">
              <div className="text-xs uppercase tracking-wide text-[#0B0B0B]/60 mb-2">{exercise.category}</div>
              <h1 className="text-2xl sm:text-4xl font-bold text-[#0B0B0B] mb-4" style={{ fontFamily: 'Clash Display' }}>{exercise.title}</h1>
              <div className="flex flex-wrap gap-2 sm:gap-4 mb-6">
                <span className="bg-[#D9C7A3] text-[#0B0B0B] px-4 py-1 rounded-full text-sm font-bold capitalize">{exercise.difficulty}</span>
                <span className="bg-gray-200 text-[#0B0B0B] px-4 py-1 rounded-full text-sm">{exercise.duration_minutes} min</span>
              </div>
            </div>

            <Card className="bg-white rounded-2xl overflow-hidden mb-6">
              <div className="aspect-video bg-black">
                {exercise.access_required ? (
                  <div className="w-full h-full flex flex-col items-center justify-center text-white p-8 text-center bg-gradient-to-br from-[#0B0B0B] to-[#161616]">
                    <div className="mb-4">
                      <svg className="w-20 h-20 text-[#C6A97C] mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Clash Display' }}>Premium Content</h3>
                    <p className="text-gray-300 mb-6">Purchase this course or subscribe to unlock this exercise video</p>
                    <Button
                      onClick={() => navigate('/courses')}
                      className="bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full px-8 py-6 font-bold"
                    >
                      View Courses
                    </Button>
                  </div>
                ) : exercise.youtube_url ? (
                  <iframe
                    data-testid="exercise-video"
                    src={getYoutubeEmbedUrl(exercise.youtube_url)}
                    title={exercise.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                ) : exercise.video_file ? (
                  <video
                    data-testid="exercise-video"
                    controls
                    preload="metadata"
                    poster={`${API}/videos/${exercise.video_file}/thumbnail`}
                    className="w-full h-full"
                  >
                    <source src={`${API}/videos/${exercise.video_file}?token=${localStorage.getItem('token')}`} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white">
                    No video available
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-6 bg-white rounded-2xl">
              <h2 className="text-2xl font-bold text-[#0B0B0B] mb-4" style={{ fontFamily: 'Clash Display' }}>About This Exercise</h2>
              <p className="text-gray-700 leading-relaxed mb-4">{exercise.description}</p>
              <div>
                <h3 className="font-bold text-[#0B0B0B] mb-2">Target Areas:</h3>
                <div className="flex gap-2 flex-wrap">
                  {exercise.body_parts?.map((part, i) => (
                    <span key={i} className="bg-gray-100 text-[#0B0B0B] px-3 py-1 rounded-full text-sm">
                      {part}
                    </span>
                  ))}
                </div>
              </div>
            </Card>
          </div>

          <div>
            <Card className="p-6 bg-white rounded-2xl sticky top-6">
              <h3 className="text-xl font-bold text-[#0B0B0B] mb-4" style={{ fontFamily: 'Clash Display' }}>Log Your Progress</h3>
              
              <div className="space-y-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="completed"
                    data-testid="completed-checkbox"
                    checked={completed}
                    onCheckedChange={setCompleted}
                  />
                  <Label htmlFor="completed" className="cursor-pointer">I completed this exercise</Label>
                </div>

                <div>
                  <Label className="mb-2 block">Pain Level: {painLevel[0]}/10</Label>
                  <Slider
                    data-testid="pain-level-slider"
                    value={painLevel}
                    onValueChange={setPainLevel}
                    max={10}
                    step={1}
                    className="mb-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>No pain</span>
                    <span>Severe pain</span>
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes" className="mb-2 block">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="notes-textarea"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="How did you feel during the exercise?"
                    rows={4}
                  />
                </div>

                <Button
                  data-testid="log-progress-btn"
                  onClick={handleLogProgress}
                  disabled={submitting}
                  className="w-full bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full py-6 font-bold"
                >
                  {submitting ? 'Saving...' : 'Save Progress'}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
